import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, ViewChild, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { ControlPoint } from 'src/app/bo/controlPoint';
import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';
import { AssignedRecords } from 'src/app/bo/vibrationsForChart/assigned-records';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/bo/site';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Blast, Hole, HoleService, PositionXYZ } from 'src/app/services/hole.service';

@Component({
  selector: 'app-assign-records-modal',
  templateUrl: './assign-records-modal.component.html',
  styleUrls: ['./assign-records-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AssignRecordsModalComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @Input() siteId: number;
  sortColumn = '';
  tirName = '';
  sortOrder = true;
  allRecords = signal<VibrationData[]>([]);
  controlPoints: ControlPoint[];
  selectedRecordIds: number[] = [];
  selectedControlPointId: number | null = null;
  serialNumberFilter = '';
  startDateFilter = '';
  endDateFilter = '';
  noteFilter = '';
  protected noteSearch$ = new Subject<string>();
  devices: any[] = [];
  blastId: number;
  errorMessage = '';
  private currentPage = 1;  
  private isLoading = false;
   constructor(
      public dialogRef: MatDialogRef<AssignRecordsModalComponent>,
      private readonly recordSevice: VibrationsRecordService,
      @Inject(MAT_DIALOG_DATA) public data: any,
       private readonly cdRef: ChangeDetectorRef, private readonly fonctionsService: FonctionsService,
      private readonly translate: TranslateService,
      private readonly holeService: HoleService
    ) {}
    
  ngOnInit(): void {
    this.allRecords.set(this.data.records); 
    this.siteId = this.data.siteId;
    this.tirName = this.data.tirName;    
    this.blastId = this.data.blastId;
    this.loadControlPoints();
    this.getDevicesNames();
    this.noteSearch$.pipe(debounceTime(500)).subscribe(() => this.onFilterChange());
    this.cdRef.detectChanges();
  }
  
  public closeDialog(): void {
    this.dialogRef.close(false);
  }

  public closeErrorMessage(): void {
    this.errorMessage = '';
    this.cdRef.detectChanges();
  }
  getDevicesNames(){
    this.recordSevice.getDeviceNames().subscribe({
      next: (devicesNames) => {
        this.devices = devicesNames;
        this.cdRef.detectChanges();
      }
    });
  }
    ngAfterViewInit() {
    this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
  }
  confirm() {
    if (this.selectedRecordIds.length === 0) {
      this.errorMessage = this.translate.instant('ERRORS_MESSAGE.SELECT_RECORDS_ERROR');
      this.cdRef.detectChanges();
      return;
    }

    const controlPoint = this.controlPoints?.find(cp => cp.controlPointId === this.selectedControlPointId);
    if (controlPoint) {
      this.holeService.GetBlastHoles(this.blastId).then((blast: Blast) => {
        const distance = this.holeService.computeMinDistanceToHoles(controlPoint, blast.Holes ?? []);
        this.submitAssignment(controlPoint, distance);
      });
    } else {
      this.submitAssignment(null, null);
    }
  }

  private submitAssignment(controlPoint: ControlPoint | null, distance: number | null): void {
    const data: AssignedRecords = {
      recordIds: this.selectedRecordIds,
      eventId: null,
      controlPointId: this.selectedControlPointId,
      blastId: this.data.blastId,
      siteId: this.siteId,
      controlPointName: controlPoint?.name,
      distance: distance ?? undefined
    };

    this.recordSevice.assignRecords(this.data.siteId, data).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
        this.dialogRef.close(true);
      },
      error: () => {
        this.fonctionsService.launch_toast(false, this.translate.instant('RECORD.ASSIGNMENT_ERROR'));
      }
    });
  }


  sortTable(column: string): void {
    if (this.sortColumn === column) {
      this.sortOrder = !this.sortOrder;
    } else {
      this.sortColumn = column;
      this.sortOrder = true;
    }
      this.allRecords.update(records => {
      const sorted = [...records];
      sorted.sort((a, b) => {
      let valA: any, valB: any;
        switch (column) {
        case 'filename':
          valA = a.recordMetadata.fileName?.toLowerCase() || '';
          valB = b.recordMetadata.fileName?.toLowerCase() || '';
          break;
        case 'date':
          valA = new Date(a.recordMetadata.timestamp);
          valB = new Date(b.recordMetadata.timestamp);
          break;
        default:
          return 0;
      }
        if (column === 'date') {
        return this.sortOrder ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
      }

        return this.sortOrder ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });
      return sorted;
    });
  }
  findMaxAbsValue(min: number, max: number) {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }
  
  loadControlPoints(): void {
    this.recordSevice.loadControlPoints(this.siteId).subscribe(response => {
      this.controlPoints = response;
      this.cdRef.detectChanges();
    })
  }
  onCheckboxChange(recordId: number, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      if (!this.selectedRecordIds.includes(recordId)) {
        this.selectedRecordIds = [...this.selectedRecordIds, recordId];
      }
    } else {
      this.selectedRecordIds = this.selectedRecordIds.filter(id => id !== recordId);
    }
    
    // Clear error message when user selects/deselects records
    if (this.errorMessage) {
      this.errorMessage = '';
      this.cdRef.detectChanges();
    }
  }
  getControlPointName(controlPointId: number): string {    
    const controlPoint = this.controlPoints?.find(cp => cp.controlPointId === controlPointId);
    if (this.controlPoints == undefined) {
      return '';
    }
    return controlPoint ? controlPoint.name : '';
  }
    onFilterChange(): void {
    this.currentPage = 1;
    const element = this.scrollContainer.nativeElement;
    element.scrollTop = 0;
    this.loadRecords();
  }
    onScroll(event: Event): void {
    const element = this.scrollContainer.nativeElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;
    if (atBottom && !this.isLoading) {
      this.currentPage++;
      this.loadRecords(this.currentPage, true);
    }
  }
    loadRecords(pageNumber = 1, append = false): void {
    if (this.isLoading) return;
    this.isLoading = true;
    const serialNumber = this.serialNumberFilter || null;
    const startDate = this.startDateFilter || null;
    const endDate = this.endDateFilter || null;
    const note = this.noteFilter || null;
    
    this.recordSevice.GetRecords(
      this.siteId, null, null, serialNumber, pageNumber, 10, false, true, true,
      startDate,
      endDate,
      note
    ).subscribe((records) => {
      if (append) {
        this.allRecords.update(curr => [...curr, ...records.data.records]);
      } else {
        this.allRecords.set(records.data.records);
      }
      this.isLoading = false;
    });
  }
}
